import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { styled } from '@mui/material/styles';
import { useCalendar, UseCalendarProps, CalendarDay } from 'hooks/useCalendar';

interface CalendarProps extends UseCalendarProps {
    onDateRangeChange?: (startDate: string | null, endDate: string | null) => void;
}

// Styled components
const CalendarContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
}));

const CalendarHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
}));

const CalendarGrid = styled(Box)({
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 0,
});

const WeekDaysGrid = styled(Box)({
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '2px',
    marginBottom: '8px',
});

const DayHeader = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    padding: theme.spacing(1),
    fontWeight: 'bold',
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
}));

const DayButton = styled(Button, {
    shouldForwardProp: (prop) => !['isSelected', 'isInRange', 'isStart', 'isEnd', 'hasStartDate', 'hasEndDate'].includes(prop as string),
})<{ 
    isSelected?: boolean, 
    isInRange?: boolean, 
    isStart?: boolean, 
    isEnd?: boolean,
    hasStartDate?: boolean,
    hasEndDate?: boolean 
}>(({ theme, isSelected, isInRange, isStart, isEnd, hasStartDate, hasEndDate }) => {
    const circularRadius = '23px';
    let borderRadius = '4px';
    
    if (isStart && isEnd) {
        borderRadius = circularRadius;
    } else if (isStart && !hasEndDate) {
        borderRadius = '4px';
    } else if (isEnd && !hasStartDate) {
        borderRadius = '4px';
    } else if (isStart) {
        borderRadius = `${circularRadius} 0 0 ${circularRadius}`;
    } else if (isEnd) {
        borderRadius = `0 ${circularRadius} ${circularRadius} 0`;
    } else if (isInRange) {
        borderRadius = '0';
    }
    
    return {
        width: '100%',
        height: '46px',
        minWidth: 'unset',
        maxWidth: '100%',
        padding: 0,
        margin: 0,
        borderRadius,
        backgroundColor: (isSelected || isInRange)
            ? theme.palette.primary.main 
            : isInRange 
                ? theme.palette.primary.light 
                : 'transparent',
        color: (isSelected || isInRange)
            ? theme.palette.primary.contrastText 
            : theme.palette.text.primary,
        fontSize: '14px',
        fontWeight: 400,
        transition: 'none',
        '&:hover': {
            backgroundColor: (isSelected || isInRange)
                ? theme.palette.primary.dark 
                : theme.palette.primary.light,
            color: theme.palette.primary.contrastText,
        },
        '&:disabled': {
            color: theme.palette.text.disabled,
        }
    };
});

const Calendar: React.FC<CalendarProps> = ({
    defaultStartDate,
    defaultEndDate,
    onDateRangeChange
}) => {
    const {
        currentDate,
        selectedStartDate,
        selectedEndDate,
        handleDayClick,
        navigateMonth,
        getCalendarDays
    } = useCalendar({ defaultStartDate, defaultEndDate });

    // Notify parent component of date range changes
    React.useEffect(() => {
        if (onDateRangeChange) {
            onDateRangeChange(selectedStartDate, selectedEndDate);
        }
    }, [selectedStartDate, selectedEndDate, onDateRangeChange]);

    const calendarDays = getCalendarDays();

    const renderDayButton = (day: CalendarDay) => {
        if (day.isEmpty) {
            return <Box key={`empty-${Math.random()}`} />;
        }

        return (
            <DayButton
                key={day.dayNumber}
                isSelected={day.isSelected}
                isInRange={day.isInRange}
                isStart={day.isStart}
                isEnd={day.isEnd}
                hasStartDate={!!selectedStartDate}
                hasEndDate={!!selectedEndDate}
                disabled={day.isFuture}
                disableRipple={true}
                onClick={() => !day.isFuture && handleDayClick(day.date)}
                aria-label={`${day.date.toLocaleDateString("en-US", { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                })}${day.isToday ? ' (today)' : ''}${day.isSelected ? ' (selected)' : ''}${day.isFuture ? ' (disabled)' : ''}`}
                aria-pressed={day.isSelected}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && !day.isFuture) {
                        e.preventDefault();
                        handleDayClick(day.date);
                    }
                }}
                sx={{
                    position: 'relative',
                    '&::after': day.isToday ? {
                        content: '""',
                        position: 'absolute',
                        bottom: '4px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '4px',
                        height: '4px',
                        borderRadius: '50%',
                        backgroundColor: 'primary.main',
                        opacity: day.isSelected ? 0 : 1
                    } : {}
                }}
            >
                {day.dayNumber}
            </DayButton>
        );
    };

    return (
        <CalendarContainer>
            {/* Screen reader instructions */}
            <Box sx={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', overflow: 'hidden' }}>
                <div aria-live="polite" id="calendar-instructions">
                    Use arrow keys to navigate between dates. Press Enter or Space to select a date. 
                    Select start and end dates to create a date range. Future dates are disabled.
                </div>
            </Box>

            {/* Calendar header */}
            <CalendarHeader>
                <IconButton 
                    onClick={() => navigateMonth('prev')}
                    size="small"
                    aria-label="Previous month"
                >
                    <ChevronLeftIcon />
                </IconButton>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    {currentDate.toLocaleString("en-US", { month: "long" })} {currentDate.getFullYear()}
                </Typography>
                <IconButton 
                    onClick={() => navigateMonth('next')}
                    size="small"
                    aria-label="Next month"
                >
                    <ChevronRightIcon />
                </IconButton>
            </CalendarHeader>

            {/* Week days */}
            <WeekDaysGrid>
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                    <DayHeader key={day}>
                        {day}
                    </DayHeader>
                ))}
            </WeekDaysGrid>

            {/* Calendar days */}
            <CalendarGrid>
                {calendarDays.map(renderDayButton)}
            </CalendarGrid>
        </CalendarContainer>
    );
};

export default Calendar;